import { z } from 'zod';
import {
  ACQUISITION_SOURCES,
  DELTA_MAX,
  DELTA_MIN,
  GAME_CODE_REGEX,
  GAME_STATUSES,
  LOCALES,
  NICKNAME_MAX,
  NICKNAME_MIN,
  SCORING_MODES,
  VERTICALS,
} from './constants.js';

export const localeSchema = z.enum(LOCALES);
export const gameStatusSchema = z.enum(GAME_STATUSES);
export const verticalSchema = z.enum(VERTICALS);
export const scoringModeSchema = z.enum(SCORING_MODES);
export const acquisitionSourceSchema = z.enum(ACQUISITION_SOURCES);

// Code de partie : insensible à la casse en entrée, toujours affiché en majuscules (doc 02 §4).
export const gameCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(GAME_CODE_REGEX, 'INVALID_GAME_CODE');
export type GameCode = z.infer<typeof gameCodeSchema>;

export const nicknameSchema = z.string().trim().min(NICKNAME_MIN).max(NICKNAME_MAX);
export type Nickname = z.infer<typeof nicknameSchema>;

// Avatar : union discriminée builtin (catalogue interne, ex. 'fox-03') | photo uploadée.
export const avatarSchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('builtin'), key: z.string().min(1).max(40) }),
  z.strictObject({ kind: z.literal('photo'), mediaId: z.uuid() }),
]);
export type Avatar = z.infer<typeof avatarSchema>;

export const deltaSchema = z
  .number()
  .int()
  .min(DELTA_MIN)
  .max(DELTA_MAX)
  .refine((d) => d !== 0, 'DELTA_ZERO');

// Payload de score canonique (doc 02 §2.6/§3.2, arbitrage doc 00 §4.32) :
// { clientEventId, teamId | participantId, delta, reason?, clientAt } — identique WS/REST,
// mappé 1:1 sur score_events (clientAt ↔ client_ts). Cible exactement UNE entité.
export const scoreEventInputSchema = z
  .strictObject({
    clientEventId: z.uuidv7(),
    teamId: z.uuid().optional(),
    participantId: z.uuid().optional(),
    delta: deltaSchema,
    reason: z.string().max(120).optional(),
    clientAt: z.iso.datetime({ offset: true }),
  })
  .refine((e) => (e.teamId === undefined) !== (e.participantId === undefined), {
    message: 'EXACTLY_ONE_TARGET',
  });
export type ScoreEventInput = z.infer<typeof scoreEventInputSchema>;
